/**
 * Edge live-condition vs cloud workflow labels (EDGE_CLOUD incident sync plan).
 * Keep labels separate — do not merge into a single semantic.
 */

import { formatFollowUpStatusDisplayLabel } from '../domain/incidents/followUpStatusSemantics';

export type EdgeLiveLabel = 'Active' | 'Cleared' | 'Not reported';
export type FollowUpStateLabel = string;

export function formatEdgeLiveConditionLabel(
  edgeState: string | undefined | null,
): EdgeLiveLabel {
  const s = String(edgeState || '').trim().toLowerCase();
  if (s === 'active') return 'Active';
  if (s === 'cleared') return 'Cleared';
  return 'Not reported';
}

/** Workflow display for the Incident state section (operator workflow, not edge truth). */
export function formatWorkflowStateLabel(inc: {
  status?: string | null;
  ops_state?: string | null;
  canonical_labels?: { workflow_status_label?: string | null } | null;
}): string {
  const canonical = String(inc.canonical_labels?.workflow_status_label || '').trim();
  if (canonical) return canonical;
  const st = String(inc.status || '').trim().toUpperCase();
  if (st === 'OPEN') return 'Needs review';
  if (st === 'ACKED') return 'Assigned';
  if (st === 'IN_REVIEW') return 'In review';
  if (st === 'CLOSED') return 'Resolved';
  if (st === 'SUPPRESSED') return 'Suppressed';
  const ops = String(inc.ops_state || '').trim().toLowerCase();
  if (ops === 'new') return 'Needs review';
  if (ops === 'acknowledged') return 'Assigned';
  if (ops === 'in_progress') return 'In review';
  if (ops === 'resolved') return 'Resolved';
  if (ops === 'closed') return 'Resolved';
  return st || '—';
}

export function formatFollowUpStateLabel(
  value: string | undefined | null,
  workflowStatus?: string | null,
): FollowUpStateLabel {
  const label = formatFollowUpStatusDisplayLabel(value, workflowStatus);
  return label || 'Not started';
}

export function formatIncidentTimestamp(iso: string | undefined | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

/**
 * Subtle UX copy when edge and workflow diverge (honest mixed states).
 */
export function incidentStateExplanatoryNote(inc: {
  status?: string | null;
  edge_state?: string | null;
}): string | null {
  const wf = String(inc.status || '').trim().toUpperCase();
  const edge = String(inc.edge_state || '').trim().toLowerCase();
  const workflowStillOpen = wf === 'OPEN' || wf === 'ACKED' || wf === 'IN_REVIEW';

  if (edge === 'cleared' && workflowStillOpen) {
    return 'The unsafe condition has cleared on the edge, but operator workflow may still be open for review.';
  }
  if (wf === 'CLOSED' || wf === 'SUPPRESSED') {
    return 'Operator workflow is complete. Edge live condition is shown separately above.';
  }
  return null;
}
